// ─── Knowledge UI — shared primitives ────────────────────────────────────────
// Common styling + small components used across the capture modal, tray,
// backpack list, search overlay, and detail modal. Matches the WORKROAMER
// dark/purple design language used in LearnToRoamCard.

import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  BookOpen, Camera, FileSpreadsheet, FileText, Globe, Headphones,
  Image as ImageIcon, Mic, Music, StickyNote, Youtube,
} from 'lucide-react';
import type { KnowledgeContentType } from '@/types/knowledgeItem';

export const UI = {
  bg: '#05070f',
  panel: '#0C0F16',
  panelSoft: 'rgba(255,255,255,0.045)',
  border: 'rgba(255,255,255,0.08)',
  borderPurple: 'rgba(139,92,246,0.35)',
  text: '#f1f5f9',
  textDim: 'rgba(255,255,255,0.55)',
  textFaint: 'rgba(255,255,255,0.35)',
  purple: '#a855f7',
  gradient: 'linear-gradient(135deg,#8b5cf6,#3b82f6)',
  red: '#ef4444',
  green: '#22c55e',
  amber: '#f59e0b',
  font: "'Inter', system-ui, sans-serif",
} as const;

export const inputStyle: CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: `1px solid ${UI.border}`,
  borderRadius: 10,
  padding: '10px 12px',
  color: UI.text,
  fontSize: 12.5,
  fontFamily: UI.font,
  outline: 'none',
};

export const primaryBtnStyle: CSSProperties = {
  background: UI.gradient,
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '10px 16px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: UI.font,
};

export const ghostBtnStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  color: UI.textDim,
  border: `1px solid ${UI.border}`,
  borderRadius: 10,
  padding: '10px 16px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: UI.font,
};

// ─── Content-type presentation ────────────────────────────────────────────────

export const CONTENT_TYPE_META: Record<
  KnowledgeContentType,
  { label: string; color: string; Icon: typeof FileText }
> = {
  pdf:         { label: 'PDF',        color: '#ef4444', Icon: FileText },
  document:    { label: 'Document',   color: '#3b82f6', Icon: FileText },
  spreadsheet: { label: 'Sheet',      color: '#22c55e', Icon: FileSpreadsheet },
  ebook:       { label: 'eBook',      color: '#f59e0b', Icon: BookOpen },
  text:        { label: 'Text',       color: '#94a3b8', Icon: FileText },
  website:     { label: 'Website',    color: '#22c55e', Icon: Globe },
  youtube:     { label: 'YouTube',    color: '#ef4444', Icon: Youtube },
  podcast:     { label: 'Podcast',    color: '#a855f7', Icon: Headphones },
  audio:       { label: 'Audio',      color: '#f59e0b', Icon: Music },
  voice:       { label: 'Voice',      color: '#f59e0b', Icon: Mic },
  photo:       { label: 'Photo',      color: '#3b82f6', Icon: Camera },
  note:        { label: 'Note',       color: '#60a5fa', Icon: StickyNote },
};

export function TypeBadge({ type, size = 9 }: { type: KnowledgeContentType; size?: number }) {
  const meta = CONTENT_TYPE_META[type] ?? CONTENT_TYPE_META.text;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      background: `${meta.color}1f`, border: `1px solid ${meta.color}45`,
      color: meta.color, borderRadius: 5, padding: '1.5px 5px',
      fontSize: size, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
      flexShrink: 0,
    }}>
      <meta.Icon style={{ width: size + 1, height: size + 1 }} />
      {meta.label}
    </span>
  );
}

export function TypeThumb({
  type, thumbnailUrl, size = 42,
}: { type: KnowledgeContentType; thumbnailUrl: string | null; size?: number }) {
  const meta = CONTENT_TYPE_META[type] ?? CONTENT_TYPE_META.text;
  if (thumbnailUrl) {
    return (
      <img
        src={thumbnailUrl}
        alt=""
        loading="lazy"
        style={{
          width: size, height: size, borderRadius: 8, objectFit: 'cover',
          border: `1px solid ${UI.border}`, flexShrink: 0, background: UI.panel,
        }}
        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: 8, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `${meta.color}18`, border: `1px solid ${meta.color}40`,
    }}>
      <meta.Icon style={{ width: size * 0.42, height: size * 0.42, color: meta.color }} />
    </span>
  );
}

const PhotoIconAlias = ImageIcon; // keep lucide import stable if needed elsewhere
void PhotoIconAlias;

// ─── Time formatting ──────────────────────────────────────────────────────────

export function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

// ─── Modal overlay (portal) ───────────────────────────────────────────────────

export function Overlay({
  onClose, children, maxWidth = 420, zIndex = 9200,
}: {
  onClose: () => void;
  children: ReactNode;
  maxWidth?: number;
  zIndex?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex,
        background: 'rgba(2,4,10,0.78)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 14, fontFamily: UI.font,
      }}
    >
      <div style={{
        width: '100%', maxWidth, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        background: UI.bg, color: UI.text,
        border: `1px solid ${UI.borderPurple}`, borderRadius: 16,
        boxShadow: '0 0 60px rgba(139,92,246,0.14), 0 8px 40px rgba(0,0,0,0.6)',
        overflow: 'hidden',
      }}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function ModalHeader({
  icon, title, subtitle, onClose,
}: { icon: ReactNode; title: string; subtitle?: string; onClose: () => void }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '13px 14px', borderBottom: `1px solid ${UI.border}`, flexShrink: 0,
    }}>
      {icon}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: 0.3 }}>{title}</p>
        {subtitle ? (
          <p style={{ margin: '2px 0 0', fontSize: 10, color: UI.textDim }}>{subtitle}</p>
        ) : null}
      </div>
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          background: 'rgba(255,255,255,0.07)', border: `1px solid ${UI.border}`,
          borderRadius: 8, width: 28, height: 28, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: UI.textDim, fontSize: 14, lineHeight: 1, flexShrink: 0,
        }}
      >
        ✕
      </button>
    </div>
  );
}
